// Readiness field copy — the SINGLE source for both surfaces, folding the two
// maps that used to drift: `copy.ts` FIELD_COPY (question-screen nouns:
// User/Capability/Outcome) and `ReadinessCard` FIELD_LABELS (card story-grammar:
// Role/I want/So that). They stay deliberately DIFFERENT per surface — `label`
// is the question-screen noun, `cardLabel` the inline-card token — but now live
// in one place, so they can't silently diverge. Keys: field.<name>.<slot>.

import type { MsgSection } from "./types.js";

export const fields: MsgSection = {
  // role
  "field.role.label": { en: "User", de: "Nutzer" },
  "field.role.cardLabel": { en: "Role", de: "Rolle" },
  "field.role.kicker": { en: "Who", de: "Wer" },
  "field.role.help": {
    en: "Who this is built for — the kind of person, team, or automated system that will actually use the result.",
    de: "Für wen das Ganze ist – die Person, das Team oder das System, das es am Ende nutzt.",
  },
  "field.role.question": { en: "Who is this for?", de: "Für wen ist das gedacht?" },
  // means
  "field.means.label": { en: "Capability", de: "Funktion" },
  "field.means.cardLabel": { en: "I want", de: "Ich möchte" },
  "field.means.kicker": { en: "What", de: "Was" },
  "field.means.help": { en: "The capability, in plain words.", de: "Was die Nutzer damit tun können, kurz und in einfachen Worten." },
  "field.means.question": {
    en: "What do the users need to be able to do?",
    de: "Was sollen die Nutzer damit tun können?",
  },
  // ends
  "field.ends.label": { en: "Outcome", de: "Ergebnis" },
  "field.ends.cardLabel": { en: "So that", de: "damit" },
  "field.ends.kicker": { en: "Why", de: "Warum" },
  "field.ends.help": {
    en: "The outcome this unlocks — the reason it's worth building.",
    de: "Was am Ende dabei herauskommt – der Grund, warum sich der Aufwand lohnt.",
  },
  "field.ends.question": { en: "Why does it matter?", de: "Warum ist das wichtig?" },
  // acceptance_criteria
  "field.acceptance_criteria.label": { en: "Acceptance criteria", de: "Akzeptanzkriterien" },
  "field.acceptance_criteria.cardLabel": { en: "Acceptance criteria", de: "Akzeptanzkriterien" },
  "field.acceptance_criteria.kicker": { en: "Done when", de: "Fertig, wenn" },
  "field.acceptance_criteria.help": {
    en: "The checks that must pass for this to count as done.",
    de: "Was stimmen muss, damit es als erledigt zählt.",
  },
  "field.acceptance_criteria.question": {
    en: "How will we know it's done?",
    de: "Woran merken wir, dass es fertig ist?",
  },
  // dod_criteria
  "field.dod_criteria.label": { en: "Definition of Done", de: "Definition of Done" },
  "field.dod_criteria.cardLabel": { en: "Definition of Done", de: "Definition of Done" },
  "field.dod_criteria.kicker": { en: "Quality bar", de: "Qualitätsanspruch" },
  "field.dod_criteria.help": {
    en: "Loom sets the quality bar from how your project is built and tested.",
    de: "Den Qualitätsanspruch leitet Loom daraus ab, wie dein Projekt gebaut und getestet wird.",
  },
  "field.dod_criteria.question": {
    en: "What would make this not done — anything beyond the feature working and your usual checks passing?",
    de: "Was wäre noch offen – über die laufende Funktion und deine üblichen Prüfungen hinaus?",
  },
  // story_points
  "field.story_points.label": { en: "Story points", de: "Story Points" },
  "field.story_points.cardLabel": { en: "Story points", de: "Story Points" },
  "field.story_points.kicker": { en: "Size", de: "Grösse" },
  "field.story_points.help": { en: "Loom estimates this from the code.", de: "Loom schätzt das aus dem Code ab." },
  "field.story_points.question": { en: "How big is this?", de: "Wie gross ist das?" },
  // milestone
  "field.milestone.label": { en: "Milestone", de: "Meilenstein" },
  "field.milestone.cardLabel": { en: "Milestone", de: "Meilenstein" },
  "field.milestone.kicker": { en: "Milestone", de: "Meilenstein" },
  "field.milestone.help": { en: "Where this lands on the roadmap.", de: "Wo das auf der Roadmap einsortiert wird." },
  "field.milestone.question": {
    en: "Which milestone should this belong to?",
    de: "Zu welchem Meilenstein gehört das?",
  },
  // priority
  "field.priority.label": { en: "Priority", de: "Priorität" },
  "field.priority.cardLabel": { en: "Priority", de: "Priorität" },
  "field.priority.kicker": { en: "Priority", de: "Priorität" },
  "field.priority.help": {
    en: "Relative to everything else waiting in the backlog.",
    de: "Im Vergleich zum Rest des Backlogs.",
  },
  "field.priority.question": { en: "How urgent is it?", de: "Wie dringend ist es?" },
  // value-source labels (was sourceLabel())
  "field.source.code": { en: "from the codebase", de: "aus dem Code" },
  "field.source.user": { en: "from what you told Loom", de: "aus deinen Angaben" },
  "field.source.request": { en: "from your request", de: "aus deiner Anfrage" },
  "field.source.meeting": { en: "from the meeting", de: "aus dem Meeting" },
  // priority option display labels (the .value send-text stays English-frozen)
  "priority.high": { en: "High", de: "Hoch" },
  "priority.normal": { en: "Normal", de: "Normal" },
  "priority.low": { en: "Low", de: "Niedrig" },
};
